import { useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FormikProps } from "formik";
import * as L from "leaflet";
import { compact } from "lodash-es";
import { useTranslation } from "react-i18next";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useMachine } from "@xstate/react";

import { HistoryState } from "src/types";
import { useRoadTypes, useVehicleTypes } from "src/hooks";
import { useSegments } from "src/hooks/segments/useSegments";
import { useCase } from "src/hooks/useCase";
import { queryFn, useScenario } from "src/hooks/useScenario";
import { isReadOnly } from "src/utils/permission.utils";
import { SegmentBulkForm, SegmentBulkFormData } from "src/forms/SegmentBulkForm";

import { SegmentBulkMutationData, useSegmentBulkUpdateMutation } from "src/hooks/segments/useSegmentBulkUpdateMutation";
import { segmentBulkEditMachine } from "src/machines/segments-bulk-edit.machine";
import { prepareVehicleCounts, toExpressionList } from "src/utils/segments-bulk-expression.utils";
import { useSegmentBulkDeleteMutation } from "src/hooks/segments/useSegmentBulkDeleteMutation";
import { useConfirm } from "material-ui-confirm";
import { ButtonLoading } from "src/components/ButtonLoading";
import { Box } from "@mui/system";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useQueryClient } from "react-query";
import { useTimefactors } from "src/hooks/timefactorHooks";

interface Props {
  map: L.Map;
  onClose: (action?: HistoryState["action"]) => void;
}

export function TrafficEmissionSegmentBulkEditPage(props: Props) {
  const { t } = useTranslation();
  const {
    caseId = "",
    scenarioId = "",
    segmentId = "",
  } = useParams<{
    caseId: string;
    scenarioId: string;
    segmentId: string;
  }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const segmentBulkUpdateMutation = useSegmentBulkUpdateMutation(scenarioId);

  const [state, send] = useMachine(segmentBulkEditMachine, {
    actions: {
      exit: () => props.onClose(),
      reloadSegments: () => {
        return queryClient.refetchQueries(["scenarios", scenarioId, "segments"], { active: true, exact: true });
      },
    },
    services: {
      fetchUpdateStatus: () => {
        const result = queryClient.fetchQuery(["scenarios", scenarioId], () => queryFn(scenarioId));
        return result;
      },
      saveSegments: (_context, event: { payload: SegmentBulkMutationData }) => {
        return segmentBulkUpdateMutation.mutateAsync(event.payload);
      },
    },
    // devTools: true,
  });

  const { data: acase } = useCase(caseId);
  const { data: scenario } = useScenario(scenarioId);
  const { data: roadTypes } = useRoadTypes();
  const { data: vehicleTypes } = useVehicleTypes(scenarioId);
  const { data: timefactors } = useTimefactors();
  const { data: allSegments } = useSegments(scenarioId);
  const confirm = useConfirm();

  const formRef = useRef<FormikProps<SegmentBulkFormData> | null>(null);

  const deleteMutation = useSegmentBulkDeleteMutation(scenarioId);

  const segmentsToEdit = useMemo(() => {
    if (!allSegments) return [];
    const selectedSegmentIds = location.state.bulkEdit || [];
    return allSegments?.filter((segment) => selectedSegmentIds.includes(segment.segmentId));
  }, [allSegments, location.state.bulkEdit]);

  /**
   * Effects
   */
  // useEffect(
  //   function logStateValues() {
  //     const subscription = service.subscribe((state) => {
  //       // simple state logging
  //       console.log(state.value, state);
  //     });

  //     return subscription.unsubscribe;
  //   },
  //   [service]
  // ); // note: service should never change

  /**
   * Functions
   */

  function handleSubmitForm(data: SegmentBulkFormData) {
    const propertyExpressionList = toExpressionList(prepareVehicleCounts(data));
    send({
      type: "SUBMIT_FORM",
      payload: {
        segmentIdList: location.state.bulkEdit || [],
        propertyExpressionList,
        useDefaultValues: false,
      },
    });
  }

  function handleSubmitValidationReport() {
    send({
      type: "SUBMIT_FORM",
      payload: {
        ...state.context.formData,
        useDefaultValues: true,
      },
    });
  }

  async function handleDelete() {
    const segmentsToDelete = segmentsToEdit.map((s) => s.segmentId);

    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteSegmentsWithCount", { count: segmentsToDelete.length }),
    })
      .then(() => deleteMutation.mutateAsync(segmentsToDelete))
      .then(() => send({ type: "CANCEL" }))
      .then(() => props.onClose("rerenderWMS"));
  }

  return render();

  function render() {
    if (state.matches("initialStatusCheck")) return renderInitialStatusCheck();
    if (state.matches("form")) return renderForm();
    if (state.matches("validationSummary")) return renderValidationSummary();
    if (state.matches("pollingUpdateStatus")) return renderPollingUpdateStatus();
    else {
      console.warn('No state match in "render" function, check this condition.');
      return null;
    }
  }

  function renderInitialStatusCheck() {
    return (
      <Box py={2}>
        <LinearProgress />
      </Box>
    );
  }

  function renderForm() {
    if (!acase || !scenario || !timefactors) return null;

    const errors = compact([
      ...(state.context.bulkUpdateResponse?.validationReportSummary.errorMessageList || []),
      ...(state.context.error || []),
    ]);

    return (
      <>
        <SegmentBulkForm
          timefactors={timefactors || []}
          key={segmentId}
          title={t("label.bulkEditSegments", { count: segmentsToEdit.length })}
          initialValues={state.context.formData}
          segments={segmentsToEdit}
          timefactorType={acase.timefactorType}
          vehicleTypes={vehicleTypes || []}
          roadTypes={roadTypes || []}
          readOnly={isReadOnly(scenario.permissions.updateEmission)}
          isLoading={state.matches("form.saving")}
          onSubmit={handleSubmitForm}
          onClose={() => send({ type: "CANCEL" })}
          onDelete={handleDelete}
          errors={errors}
          formRef={formRef}
        />
      </>
    );
  }

  function renderValidationSummary() {
    return (
      <Stack spacing={3}>
        <Typography variant="h2">{t("label.validationSummary")}</Typography>

        <Typography variant="body1">{t("label.validationSummaryDescription")} </Typography>

        <div>
          <ServerErrors data={state.context.bulkUpdateResponse.validationReportSummary.errorMessageList} />
        </div>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="text" onClick={() => send({ type: "CANCEL" })}>
            {t("label.cancel")}
          </Button>
          <Button variant="text" onClick={() => send({ type: "PREVIOUS" })}>
            {t("label.editErrors")}
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonLoading
            isLoading={state.matches("validationSummary.saving")}
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSubmitValidationReport}
          >
            {t("label.saveWithDefaults")}
          </ButtonLoading>
        </Box>
      </Stack>
    );
  }

  function renderPollingUpdateStatus() {
    return (
      <div>
        <Typography variant="h2">{t("label.updating")}</Typography>
        <Typography variant="body1">{t("label.updatingSelectedSegments")}</Typography>
        <Box py={2}>
          <LinearProgress />
        </Box>
      </div>
    );
  }
}
